import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useProfile } from '@/hooks/useProfile';
import {
    type PaymentMethod,
    TradingAccountDepositRequest,
} from '@/api/transactions/use-trading-account-transactions';
import { isManualPaymentMethod } from '@/lib/paymentMethodUtils';
import {
    ExternalLink,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Zap,
    Shield,
    CreditCard,
} from 'lucide-react';

interface TradingAccountDepositFormProps {
    account: any;
    paymentMethods: PaymentMethod[];
    onSubmit: (data: TradingAccountDepositRequest) => Promise<any>;
    isLoading: boolean;
    onCancel: () => void;
}

interface ProcessorResponse {
    transaction?: any;
    payment_url?: string;
    widget_url?: string;
    form_data?: Record<string, any>;
    method?: 'redirect' | 'form' | 'widget' | string;
    processor?: string;
    error?: string;
}

const TradingAccountDepositForm: React.FC<TradingAccountDepositFormProps> = ({
    account,
    paymentMethods,
    onSubmit,
    isLoading,
    onCancel,
}) => {
    const { t } = useTranslation('accounts');
    const { profile } = useProfile();
    const [formData, setFormData] = useState({
        amount: '',
        payment_method_id: '',
        deposit_manual_config_id: '',
        payment_details: {} as Record<string, any>,
        notes: '',
    });

    const [paymentDetails, setPaymentDetails] = useState({
        address: '',
        holder_name: '',
        iban: '',
        card_number: '',
        card_holder: '',
        expiry_date: '',
        cvv: '',
        customer_first_name: '',
        customer_last_name: '',
        customer_email: '',
        customer_dob: '',
        customer_address: '',
        customer_city: '',
        customer_country: '',
        customer_zip_code: '',
    });

    const [processorResponse, setProcessorResponse] = useState<ProcessorResponse | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [manualLoading, setManualLoading] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);

    const filteredPaymentMethods = paymentMethods.filter(pm =>
        pm.supported_currency_symbols?.includes(account?.base_currency)
    );

    const selectedPaymentMethod = filteredPaymentMethods.find(
        pm => pm.id === formData.payment_method_id
    );
    const paymeshFieldLabels: Record<string, string> = {
        customer_first_name: 'First name',
        customer_last_name: 'Last name',
        customer_email: 'Email',
        customer_dob: 'Date of birth',
        customer_address: 'Address',
        customer_city: 'City',
        customer_country: 'Country',
        customer_zip_code: 'ZIP code',
    };
    const requiresPayMeshCustomerFields = useMemo(() => {
        if (!selectedPaymentMethod || isManualPaymentMethod(selectedPaymentMethod)) {
            return false;
        }
        if (selectedPaymentMethod.supports_processors) {
            // Fallback: processor-backed methods may resolve to PayMesh at runtime.
            return true;
        }
        const haystack = [
            selectedPaymentMethod.name,
            selectedPaymentMethod.code,
            selectedPaymentMethod.display_name,
            selectedPaymentMethod.description,
            JSON.stringify(selectedPaymentMethod.processor_config || {}),
            JSON.stringify(selectedPaymentMethod.metadata || {}),
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
        return haystack.includes('paymesh') || haystack.includes('pay-mesh');
    }, [selectedPaymentMethod]);

    useEffect(() => {
        if (!requiresPayMeshCustomerFields) {
            return;
        }
        setPaymentDetails(prev => ({
            ...prev,
            customer_first_name: prev.customer_first_name || profile.firstName || '',
            customer_last_name: prev.customer_last_name || profile.lastName || '',
            customer_email: prev.customer_email || profile.email || '',
            customer_address: prev.customer_address || profile.address || '',
            customer_city: prev.customer_city || profile.city || '',
            customer_country: prev.customer_country || profile.country || '',
            customer_zip_code: prev.customer_zip_code || profile.postalCode || '',
        }));
    }, [profile, requiresPayMeshCustomerFields]);

    const stablecoinToFiatMap: Record<string, string> = {
        USDT: 'USD',
        USDC: 'USD',
        EURC: 'EUR',
        EURT: 'EUR',
    };

    const manualConfigs = selectedPaymentMethod?.manual_currency_configs || [];
    const relevantManualConfigs = useMemo(() => {
        if (!isManualPaymentMethod(selectedPaymentMethod)) return [];
        const accountCurrency = String(account?.base_currency || '').toUpperCase();
        if ((selectedPaymentMethod?.method_category || 'fiat') === 'crypto') {
            return manualConfigs.filter(c => {
                const symbol = String(c.currency?.symbol || '').toUpperCase();
                return stablecoinToFiatMap[symbol] === accountCurrency;
            });
        }
        return manualConfigs.filter(
            c => String(c.currency?.symbol || '').toUpperCase() === accountCurrency
        );
    }, [manualConfigs, selectedPaymentMethod, account?.base_currency]);

    const selectedManualConfig = useMemo(() => {
        if (!formData.deposit_manual_config_id) return undefined;
        return relevantManualConfigs.find(c => c.id === formData.deposit_manual_config_id);
    }, [relevantManualConfigs, formData.deposit_manual_config_id]);

    useEffect(() => {
        if (!isManualPaymentMethod(selectedPaymentMethod)) return;
        if (relevantManualConfigs.length === 1) {
            setFormData(prev => ({
                ...prev,
                deposit_manual_config_id: relevantManualConfigs[0].id,
            }));
            return;
        }
        if (
            formData.deposit_manual_config_id &&
            !relevantManualConfigs.some(c => c.id === formData.deposit_manual_config_id)
        ) {
            setFormData(prev => ({ ...prev, deposit_manual_config_id: '' }));
        }
    }, [selectedPaymentMethod?.id, relevantManualConfigs, formData.deposit_manual_config_id]);

    useEffect(() => {
        if (!isManualPaymentMethod(selectedPaymentMethod) || !formData.deposit_manual_config_id) {
            setManualLoading(false);
            return;
        }
        setManualLoading(true);
        const timer = window.setTimeout(() => setManualLoading(false), 900);
        return () => window.clearTimeout(timer);
    }, [formData.deposit_manual_config_id, selectedPaymentMethod?.id]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsProcessing(true);
        setProcessorResponse(null);

        try {
            const manual = isManualPaymentMethod(selectedPaymentMethod);
            const missingPayMeshFields = requiresPayMeshCustomerFields
                ? (
                      [
                          'customer_first_name',
                          'customer_last_name',
                          'customer_email',
                          'customer_dob',
                          'customer_address',
                          'customer_city',
                          'customer_country',
                          'customer_zip_code',
                      ] as const
                  ).filter(key => !String(paymentDetails[key] || '').trim())
                : [];

            if (missingPayMeshFields.length > 0) {
                setProcessorResponse({
                    error: `Please complete required PayMesh details: ${missingPayMeshFields
                        .map(key => paymeshFieldLabels[key])
                        .join(', ')}`,
                });
                return;
            }
            const request: TradingAccountDepositRequest = {
                trading_account_id: account.id,
                amount: parseFloat(formData.amount),
                payment_method_id: formData.payment_method_id,
                deposit_currency_id: manual ? selectedManualConfig?.currency_id : undefined,
                deposit_manual_config_id: manual ? formData.deposit_manual_config_id : undefined,
                payment_details:
                    !manual &&
                    (selectedPaymentMethod?.requires_address ||
                        selectedPaymentMethod?.requires_card_details ||
                        selectedPaymentMethod?.requires_bank_details ||
                        requiresPayMeshCustomerFields)
                        ? {
                              ...paymentDetails,
                              holder_name: paymentDetails.holder_name,
                              card_holder: paymentDetails.card_holder || paymentDetails.holder_name,
                              account_holder:
                                  (paymentDetails as any).account_holder ||
                                  paymentDetails.holder_name,
                              iban: paymentDetails.iban || undefined,
                          }
                        : undefined,
                notes: formData.notes || undefined,
            };

            const response = await onSubmit(request);

            if (response?.payment_url || response?.widget_url || response?.form_data) {
                setProcessorResponse(response);

                if (response.form_data && response.payment_url && response.method === 'POST') {
                    setTimeout(() => {
                        submitPGPayForm(response.payment_url!, response.form_data!);
                    }, 1000);
                }
            } else {
                const tx = response?.transaction ?? response;
                setProcessorResponse({
                    transaction: tx,
                });
            }
        } catch (error: any) {
            setProcessorResponse({
                error:
                    error?.response?.data?.message || error?.message || t('failedToCreateDeposit'),
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const submitPGPayForm = (url: string, formDataPost: Record<string, any>) => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = url;
        form.target = '_blank';

        Object.entries(formDataPost).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = String(value);
            form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    const handleRedirectToPayment = () => {
        if (processorResponse?.payment_url) {
            window.open(processorResponse.payment_url, '_blank');
        }
    };

    const handleOpenWidget = () => {
        if (processorResponse?.widget_url) {
            window.open(processorResponse.widget_url, '_blank', 'width=500,height=700');
        }
    };

    const handlePaymentMethodChange = (value: string) => {
        setFormData(prev => ({ ...prev, payment_method_id: value, deposit_manual_config_id: '' }));
        setPaymentDetails({
            address: '',
            holder_name: '',
            iban: '',
            card_number: '',
            card_holder: '',
            expiry_date: '',
            cvv: '',
            customer_first_name: '',
            customer_last_name: '',
            customer_email: '',
            customer_dob: '',
            customer_address: '',
            customer_city: '',
            customer_country: '',
            customer_zip_code: '',
        });
    };

    const needsManualCurrency = isManualPaymentMethod(selectedPaymentMethod);
    const manualStepComplete =
        !needsManualCurrency ||
        (!!formData.deposit_manual_config_id && !manualLoading && !!selectedManualConfig);

    const submitDisabled =
        isLoading ||
        isProcessing ||
        !formData.amount ||
        !formData.payment_method_id ||
        !manualStepComplete;

    const showManualInstructions =
        needsManualCurrency &&
        formData.deposit_manual_config_id &&
        !manualLoading &&
        !!selectedManualConfig;

    // Processor redirect / widget
    if (
        processorResponse &&
        (processorResponse.payment_url ||
            processorResponse.widget_url ||
            processorResponse.form_data)
    ) {
        const isPGPay = processorResponse.processor === 'pgpay';
        const isTransak = processorResponse.processor === 'transak';
        const isPayMesh = processorResponse.processor === 'paymesh';

        return (
            <div className="space-y-4">
                <Card className="border-2 border-primary/20 bg-gradient-to-br from-background to-primary/5">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            {isPGPay ? (
                                <Zap className="h-6 w-6 text-primary" />
                            ) : isTransak ? (
                                <Shield className="h-6 w-6 text-primary" />
                            ) : isPayMesh ? (
                                <CreditCard className="h-6 w-6 text-primary" />
                            ) : (
                                <CheckCircle2 className="h-6 w-6 text-primary" />
                            )}
                            <div>
                                <CardTitle>{t('redirectingToPayment')}</CardTitle>
                                <CardDescription>{t('redirectingDescription')}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {processorResponse.form_data && processorResponse.method === 'POST' ? (
                            <Alert>
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    Redirecting to PGPay payment page. A new window will open
                                    shortly.
                                </AlertDescription>
                            </Alert>
                        ) : processorResponse.widget_url ? (
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                    Complete your payment using the Transak widget below.
                                </p>
                                <div className="border rounded-lg overflow-hidden bg-background">
                                    <iframe
                                        src={processorResponse.widget_url}
                                        className="w-full h-[600px] border-0"
                                        title="Transak Payment Widget"
                                        allow="camera; microphone; payment"
                                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                                    />
                                </div>
                                <Button
                                    variant="outline"
                                    onClick={handleOpenWidget}
                                    className="w-full"
                                >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Open in New Window
                                </Button>
                            </div>
                        ) : processorResponse.payment_url ? (
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                    Click the button below to complete your payment.
                                </p>
                                <Button
                                    onClick={handleRedirectToPayment}
                                    className="w-full"
                                    size="lg"
                                >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Click Here to Continue Your Deposit
                                </Button>
                            </div>
                        ) : null}

                        {processorResponse.transaction && (
                            <div className="pt-4 border-t">
                                <p className="text-sm text-muted-foreground">
                                    Transaction ID:{' '}
                                    <span className="font-mono">
                                        {processorResponse.transaction.id}
                                    </span>
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Manual / non-processor success
    if (
        processorResponse?.transaction &&
        !processorResponse.payment_url &&
        !processorResponse.widget_url &&
        !processorResponse.form_data &&
        !processorResponse.error
    ) {
        return (
            <div className="space-y-4">
                <Card className="border-2 border-green-500/30 bg-gradient-to-br from-background to-green-500/5">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <CheckCircle2 className="h-6 w-6 text-green-500" />
                            <div>
                                <CardTitle>
                                    {t('depositSubmitted') || 'Deposit request submitted'}
                                </CardTitle>
                                <CardDescription>
                                    {t('depositSubmittedDescription') ||
                                        'Your deposit request has been recorded. Complete the transfer using the instructions you reviewed.'}
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-muted-foreground">
                            Transaction ID:{' '}
                            <span className="font-mono">{processorResponse.transaction.id}</span>
                        </p>
                        <Button
                            variant="outline"
                            className="w-full mt-4"
                            onClick={() => {
                                setProcessorResponse(null);
                                setFormData({
                                    amount: '',
                                    payment_method_id: '',
                                    deposit_manual_config_id: '',
                                    payment_details: {},
                                    notes: '',
                                });
                            }}
                        >
                            Create Another Deposit
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (processorResponse?.error) {
        return (
            <div className="space-y-4">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{processorResponse.error}</AlertDescription>
                </Alert>
                <Button
                    variant="outline"
                    onClick={() => {
                        setProcessorResponse(null);
                    }}
                    className="w-full"
                >
                    Try Again
                </Button>
            </div>
        );
    }

    const details = selectedManualConfig?.details || {};

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            <div>
                <Label htmlFor="amount">
                    {t('amountLabel')} ({account?.base_currency})
                </Label>
                <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={t('enterAmount')}
                    value={formData.amount}
                    onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary"
                    required
                />
            </div>

            <div>
                <Label htmlFor="payment_method">{t('paymentMethod')}</Label>
                <Select
                    value={formData.payment_method_id}
                    onValueChange={handlePaymentMethodChange}
                    disabled={filteredPaymentMethods.length === 0}
                >
                    <SelectTrigger className="bg-background border-border text-foreground focus:border-primary">
                        <SelectValue placeholder={t('selectPaymentMethod')} />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                        {filteredPaymentMethods.map(method => (
                            <SelectItem
                                key={method.id}
                                value={method.id}
                                className="text-foreground hover:bg-accent hover:text-accent-foreground"
                            >
                                {method.display_name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {filteredPaymentMethods.length === 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('noPaymentMethodsForCurrency', { currency: account?.base_currency })}
                    </p>
                )}
                {selectedPaymentMethod?.note && (
                    <Alert className="mt-2">
                        <AlertDescription>{selectedPaymentMethod.note}</AlertDescription>
                    </Alert>
                )}
            </div>

            {isManualPaymentMethod(selectedPaymentMethod) && relevantManualConfigs.length > 0 && (
                <div className="space-y-2">
                    {relevantManualConfigs.length > 1 && (
                        <Select
                            value={formData.deposit_manual_config_id}
                            onValueChange={v =>
                                setFormData(prev => ({ ...prev, deposit_manual_config_id: v }))
                            }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder={t('selectCurrency')} />
                            </SelectTrigger>
                            <SelectContent>
                                {relevantManualConfigs.map((c, idx) => {
                                    const label =
                                        (c.currency?.symbol || c.currency_id) +
                                        (c.detail_type === 'crypto' && c.details?.network
                                            ? ` - ${c.details.network}`
                                            : c.detail_type === 'bank' && c.details?.bank_name
                                              ? ` - ${c.details.bank_name}`
                                              : ` - ${c.detail_type}`);
                                    return (
                                        <SelectItem
                                            key={c.id || `${c.currency_id}-${idx}`}
                                            value={c.id}
                                        >
                                            {label}
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    )}
                    {formData.deposit_manual_config_id && manualLoading && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t('loadingInstructions')}
                        </div>
                    )}
                    {showManualInstructions && selectedManualConfig && (
                        <Card className="border-muted">
                            <CardHeader className="py-3">
                                <CardTitle className="text-sm">
                                    Transfer instructions ({selectedManualConfig.currency?.symbol})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 text-sm">
                                {selectedManualConfig.detail_type === 'bank' && (
                                    <div className="space-y-1">
                                        {details.bank_name && (
                                            <p>
                                                <span className="text-muted-foreground">
                                                    Bank:{' '}
                                                </span>
                                                {details.bank_name}
                                            </p>
                                        )}
                                        {details.iban && (
                                            <p>
                                                <span className="text-muted-foreground">
                                                    IBAN:{' '}
                                                </span>
                                                {details.iban}
                                            </p>
                                        )}
                                        {details.account_number && (
                                            <p>
                                                <span className="text-muted-foreground">
                                                    Account:{' '}
                                                </span>
                                                {details.account_number}
                                            </p>
                                        )}
                                        {details.swift && (
                                            <p>
                                                <span className="text-muted-foreground">
                                                    SWIFT:{' '}
                                                </span>
                                                {details.swift}
                                            </p>
                                        )}
                                        {details.branch_address && (
                                            <p>
                                                <span className="text-muted-foreground">
                                                    Branch:{' '}
                                                </span>
                                                {details.branch_address}
                                            </p>
                                        )}
                                        {!details.bank_name &&
                                            !details.iban &&
                                            !details.account_number &&
                                            (details.instructions || details.text) && (
                                                <p className="whitespace-pre-wrap">
                                                    {details.instructions || details.text}
                                                </p>
                                            )}
                                    </div>
                                )}
                                {selectedManualConfig.detail_type === 'crypto' && (
                                    <div className="space-y-3">
                                        {details.network && (
                                            <p>
                                                <span className="text-muted-foreground">
                                                    Network:{' '}
                                                </span>
                                                {details.network}
                                            </p>
                                        )}
                                        {details.memo && (
                                            <p>
                                                <span className="text-muted-foreground">
                                                    Memo:{' '}
                                                </span>
                                                {details.memo}
                                            </p>
                                        )}
                                        {details.wallet_address && (
                                            <>
                                                <div className="flex justify-center rounded-lg border bg-white p-3 w-fit mx-auto">
                                                    <QRCode
                                                        value={String(details.wallet_address)}
                                                        size={160}
                                                        level="M"
                                                    />
                                                </div>
                                                <p className="break-all font-mono text-xs text-center">
                                                    {details.wallet_address}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                )}
                                {selectedManualConfig.detail_type === 'credit_card' && (
                                    <div className="space-y-1">
                                        {details.card_holder && (
                                            <p>
                                                <span className="text-muted-foreground">
                                                    Card holder:{' '}
                                                </span>
                                                {details.card_holder}
                                            </p>
                                        )}
                                        {details.card_number && (
                                            <p>
                                                <span className="text-muted-foreground">
                                                    Card number:{' '}
                                                </span>
                                                {details.card_number}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {selectedManualConfig.detail_type === 'custom' && (
                                    <p className="whitespace-pre-wrap">
                                        {details.instructions || details.text}
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {!isManualPaymentMethod(selectedPaymentMethod) &&
                selectedPaymentMethod?.requires_address && (
                    <div>
                        <Label htmlFor="address">{t('address')}</Label>
                        <Input
                            id="address"
                            placeholder={t('enterAddress')}
                            value={paymentDetails.address}
                            onChange={e =>
                                setPaymentDetails(prev => ({ ...prev, address: e.target.value }))
                            }
                            className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary"
                            required
                        />
                    </div>
                )}

            {!isManualPaymentMethod(selectedPaymentMethod) &&
                selectedPaymentMethod?.requires_holder_name && (
                    <div>
                        <Label htmlFor="holder_name">{t('holderName')}</Label>
                        <Input
                            id="holder_name"
                            placeholder={t('holderNamePlaceholder')}
                            value={paymentDetails.holder_name}
                            onChange={e =>
                                setPaymentDetails(prev => ({
                                    ...prev,
                                    holder_name: e.target.value,
                                }))
                            }
                            className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary"
                            required
                        />
                    </div>
                )}

            {!isManualPaymentMethod(selectedPaymentMethod) &&
                selectedPaymentMethod?.requires_bank_details && (
                    <div>
                        <Label htmlFor="iban">{t('iban')}</Label>
                        <Input
                            id="iban"
                            placeholder={t('enterIban')}
                            value={paymentDetails.iban}
                            onChange={e =>
                                setPaymentDetails(prev => ({ ...prev, iban: e.target.value }))
                            }
                            className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary"
                            required
                        />
                    </div>
                )}

            {!isManualPaymentMethod(selectedPaymentMethod) &&
                selectedPaymentMethod?.requires_card_details && (
                    <div className="space-y-3">
                        <div>
                            <Label htmlFor="card_number">{t('cardNumber')}</Label>
                            <Input
                                id="card_number"
                                placeholder={t('enterCardNumber')}
                                value={paymentDetails.card_number}
                                onChange={e =>
                                    setPaymentDetails(prev => ({
                                        ...prev,
                                        card_number: e.target.value,
                                    }))
                                }
                                className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="expiry_date">{t('expiryDate')}</Label>
                                <Input
                                    id="expiry_date"
                                    placeholder={t('expiryPlaceholder')}
                                    value={paymentDetails.expiry_date}
                                    onChange={e =>
                                        setPaymentDetails(prev => ({
                                            ...prev,
                                            expiry_date: e.target.value,
                                        }))
                                    }
                                    className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="cvv">{t('cvv')}</Label>
                                <Input
                                    id="cvv"
                                    placeholder={t('cvv')}
                                    value={paymentDetails.cvv}
                                    onChange={e =>
                                        setPaymentDetails(prev => ({
                                            ...prev,
                                            cvv: e.target.value,
                                        }))
                                    }
                                    className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                )}

            {requiresPayMeshCustomerFields && (
                <div className="space-y-3">
                    <div className="text-sm font-medium">PayMesh required details</div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="customer_first_name">First Name</Label>
                            <Input
                                id="customer_first_name"
                                value={paymentDetails.customer_first_name}
                                onChange={e =>
                                    setPaymentDetails(prev => ({
                                        ...prev,
                                        customer_first_name: e.target.value,
                                    }))
                                }
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="customer_last_name">Last Name</Label>
                            <Input
                                id="customer_last_name"
                                value={paymentDetails.customer_last_name}
                                onChange={e =>
                                    setPaymentDetails(prev => ({
                                        ...prev,
                                        customer_last_name: e.target.value,
                                    }))
                                }
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="customer_email">Email</Label>
                        <Input
                            id="customer_email"
                            type="email"
                            value={paymentDetails.customer_email}
                            onChange={e =>
                                setPaymentDetails(prev => ({
                                    ...prev,
                                    customer_email: e.target.value,
                                }))
                            }
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="customer_dob">Date of Birth</Label>
                        <Input
                            id="customer_dob"
                            type="date"
                            value={paymentDetails.customer_dob}
                            onChange={e =>
                                setPaymentDetails(prev => ({
                                    ...prev,
                                    customer_dob: e.target.value,
                                }))
                            }
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="customer_address">Address</Label>
                        <Input
                            id="customer_address"
                            value={paymentDetails.customer_address}
                            onChange={e =>
                                setPaymentDetails(prev => ({
                                    ...prev,
                                    customer_address: e.target.value,
                                }))
                            }
                            required
                        />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label htmlFor="customer_city">City</Label>
                            <Input
                                id="customer_city"
                                value={paymentDetails.customer_city}
                                onChange={e =>
                                    setPaymentDetails(prev => ({
                                        ...prev,
                                        customer_city: e.target.value,
                                    }))
                                }
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="customer_country">Country</Label>
                            <Input
                                id="customer_country"
                                placeholder="US"
                                value={paymentDetails.customer_country}
                                onChange={e =>
                                    setPaymentDetails(prev => ({
                                        ...prev,
                                        customer_country: e.target.value.toUpperCase(),
                                    }))
                                }
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="customer_zip_code">ZIP Code</Label>
                            <Input
                                id="customer_zip_code"
                                value={paymentDetails.customer_zip_code}
                                onChange={e =>
                                    setPaymentDetails(prev => ({
                                        ...prev,
                                        customer_zip_code: e.target.value,
                                    }))
                                }
                                required
                            />
                        </div>
                    </div>
                </div>
            )}

            <div>
                <Label htmlFor="notes">{t('notesOptional')}</Label>
                <Textarea
                    id="notes"
                    placeholder={t('addNotesPlaceholder')}
                    value={formData.notes}
                    onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary"
                    rows={3}
                />
            </div>

            <div className="flex gap-3 pt-4">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    className="flex-1 text-foreground border-border hover:bg-accent hover:text-accent-foreground"
                    disabled={isLoading}
                >
                    {t('cancel')}
                </Button>
                <Button
                    type="submit"
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                    disabled={submitDisabled}
                >
                    {isLoading || isProcessing ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('processing')}
                        </>
                    ) : (
                        t('submitDeposit')
                    )}
                </Button>
            </div>
        </form>
    );
};

export default TradingAccountDepositForm;
